import { Radio, Skeleton, Tooltip } from 'antd';
import { IconVerifiedTick, IconPCISecurityStandards, IconDotSeparator } from '@/assets/static/icons';
import { PaymentSource } from '@/types/generated/graphql';
import React from 'react';
import { getCardIcon } from '@modules/settings/payment-methods/utils/get-card-brands';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { AppAlert } from '@components/antd';
import { getCardStatus } from '@modules/settings/payment-methods/utils/card-validity-status';
import clsx from 'clsx';

type PaymentMethodsListProps = {
  paymentCards: (PaymentSource | null)[];
  paymentPaypal: (PaymentSource | null)[];
  selected: PaymentSource | null;
  onChange: (method: PaymentSource) => void;
  loading?: boolean;
};

export function PaymentMethodsList(props: PaymentMethodsListProps) {
  const { t } = useTranslation();
  const { paymentCards, paymentPaypal, selected, onChange, loading } = props;
  if (loading) return <PaymentMethodsList.Loading />;

  return (
    <React.Fragment>
      {paymentCards?.length > 0 && (
        <div className="space-y-1.5">
          <React.Fragment>
            <p className="text-xs font-medium text-text-500">{t('pay_via_card').toUpperCase()}</p>
            <div className="border border-gray-400 rounded-2xl divide-y divide-gray-400">
              {paymentCards.map((method) => (
                <PaymentMethodsList.PaymentCard
                  key={method?.id}
                  icon={getCardIcon(method?.card?.brand)}
                  title={`**** **** **** ${method?.card?.last4}`}
                  subtitle={`${capitalize(t('valid_until'))} ${method?.card?.expiryMonth}/${method?.card?.expiryYear}`}
                  isDefault={method?.isPrimary}
                  checked={selected?.id === method?.id}
                  onSelect={() => method && onChange(method)}
                  status={method?.status}
                />
              ))}
            </div>
          </React.Fragment>
        </div>
      )}

      {paymentPaypal?.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-text-500">{t('pay_via_paypal').toUpperCase()}</p>
          <div className="border border-gray-400 rounded-2xl divide-y divide-gray-400">
            {paymentPaypal?.map((method) => (
              <PaymentMethodsList.PaymentCard
                key={method?.id}
                icon={getCardIcon('paypal')}
                title={capitalize(t('paypal'))}
                subtitle={method?.paypal?.email}
                isDefault={method?.isPrimary}
                checked={selected?.id === method?.id}
                onSelect={() => method && onChange(method)}
                status={method?.status}
              />
            ))}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

PaymentMethodsList.Loading = () => {
  return (
    <React.Fragment>
      <Skeleton.Input active size="small" style={{ width: 160 }} />
      <div className="border border-gray-400 rounded-2xl divide-y divide-gray-400">
        {[1, 2].map((key) => (
          <div key={key} className="p-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Skeleton.Avatar active size="small" shape="circle" />
              <div>
                <Skeleton.Input active size="small" style={{ width: 240 }} />
              </div>
            </div>
            <Skeleton.Button active size="small" shape="round" />
          </div>
        ))}
      </div>
    </React.Fragment>
  );
};

type PaymentCardProps = {
  icon: React.ReactNode;
  title: string;
  subtitle?: string | null;
  isDefault?: boolean | null;
  checked: boolean;
  onSelect: () => void;
  status?: PaymentSource['status'];
};

PaymentMethodsList.PaymentCard = function PaymentCard(props: PaymentCardProps) {
  const { t } = useTranslation();
  const { icon, title, subtitle, isDefault, checked, onSelect } = props;

  const DefaultChip = (
    <span className="text-sm flex gap-2 items-center border py-1.5 px-2.5 rounded-4xl border-gray-400">
      <IconVerifiedTick /> {capitalize(t('default'))}
    </span>
  );

  const { validityStatusMessage, colorTag, tag, iconType } = getCardStatus(props?.status, t);

  return (
    <div className="p-3 flex justify-between items-center cursor-pointer" onClick={onSelect}>
      <div className="flex items-center gap-3">
        {icon}
        <div className={clsx(props?.status === 'valid' ? 'max-w-40' : 'max-w-60')}>
          <div className="font-medium text-text-800 leading-5">{title}</div>
          {subtitle && (
            <div
              className={clsx('font-normal text-sm text-text-500 leading-4.5', props?.status === 'valid' && 'truncate')}
            >
              {subtitle}
              <span style={{ color: colorTag }} className="inline-block">
                <span className="md:px-2 ">{props?.status !== 'valid' && <IconDotSeparator />}</span>
                {tag}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3.5">
        {props?.status !== 'valid' ? (
          <Tooltip title={<p className="text-xs text-slate-500">{validityStatusMessage}</p>}>
            <AppAlert
              type={iconType}
              showIcon
              className="p-0! [&_svg]:mr-0! hidden! sm:inline-block! border-none! bg-transparent!"
            />
          </Tooltip>
        ) : (
          isDefault && DefaultChip
        )}
        <Radio checked={checked} onChange={onSelect} />
      </div>
    </div>
  );
};

PaymentMethodsList.CompliantLabel = () => <CompliantLabel />;

function CompliantLabel() {
  const { t } = useTranslation();

  return (
    <div className="flex justify-center gap-2">
      <IconPCISecurityStandards />
      <p className="text-sm font-normal text-text-700">{t('pci_compliant_secure_and_encrypted')}</p>
    </div>
  );
}
